import React, {FunctionComponent, useRef, useState} from "react";
import clsx from "clsx";
import IconTooltip, {IconTooltipProps} from "../snippet/icon/IconTooltip";

export type ActionMenuItem = {
    label: string;
    onClick: (e: React.MouseEvent) => void;
    critical?: boolean;
    separator?: boolean;
    cssClass?: string;
}

type ActionsMenuProps = {
    iconTooltip: IconTooltipProps;
    items: ActionMenuItem[];
}

const ACTIONS_MENU_MARGIN_IN_PIXELS = 5;

const ActionsMenu: FunctionComponent<ActionsMenuProps> = ({iconTooltip, items}) => {
    const [cssProperties, setCssProperties] = useState<React.CSSProperties>();
    const [isActive, setActive] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuContentRef = useRef<HTMLDivElement | null>(null);

    const getValue = (value: number) => {
        return `${value}px`;
    };

    const handleShowActions = () => {
        const menuRect = menuRef.current?.getBoundingClientRect();
        const menuContentRect = menuContentRef.current?.getBoundingClientRect();

        if (window.visualViewport && menuRect && menuContentRect) {
            let topValue = menuRect.top + (menuRect.height / 2) + ACTIONS_MENU_MARGIN_IN_PIXELS;
            let leftValue = menuRect.right - (menuRect.width / 2) - menuContentRect.width - ACTIONS_MENU_MARGIN_IN_PIXELS;

            if (topValue + menuContentRect.height + ACTIONS_MENU_MARGIN_IN_PIXELS >= window.visualViewport.height) {
                topValue = menuRect.bottom - (menuRect.height / 2) - menuContentRect.height - ACTIONS_MENU_MARGIN_IN_PIXELS;
            }

            if (leftValue < ACTIONS_MENU_MARGIN_IN_PIXELS) {
                leftValue = menuRect.left + (menuRect.width / 2) + ACTIONS_MENU_MARGIN_IN_PIXELS;
            }

            setCssProperties({
                top: getValue(topValue),
                left: getValue(leftValue)
            });
            setActive(true);
        }
    };

    const handleClick = (onClick: (e: React.MouseEvent) => void, e: React.MouseEvent) => {
        onClick(e);
        setActive(false);
    };

    return (
        <div ref={menuRef} className="actions-menu">
            <IconTooltip
                {...iconTooltip}
                cssClass={clsx("actions-menu__button", iconTooltip.cssClass)}
                onClick={handleShowActions}
            />
            <div
                ref={menuContentRef}
                className={clsx("actions-menu__content", {"actions-menu__content--active": isActive})}
                style={cssProperties}
                onMouseLeave={() => setActive(false)}
            >
                <ul>
                    {items.map(({label, onClick, critical, separator, cssClass}, index) => (
                        <li key={index} onClick={(e) => handleClick(onClick, e)} className={clsx("actions-menu__content__item", {"actions-menu__content__item--with-separator": separator}, {"actions-menu__content__item--critical": critical}, cssClass)}>
                            <span>{label}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default ActionsMenu;
